import { CLAIM_STATUSES } from '../constants/claimStatus';

export class RoleBasedFiltering {
  constructor(userRoles = [], user = null) {
    this.userRoles = userRoles;
    this.user = user;
  }

  /**
   * Check if user has specific role
   */
  hasRole(role) {
    return this.userRoles?.includes(role) || false;
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles) {
    return roles.some(role => this.hasRole(role));
  }

  /**
   * Check if user is admin
   */
  isAdmin() {
    return this.hasRole('Admin');
  }

  /**
   * Check if user can submit claims
   */
  canSubmitClaims() {
    return this.hasAnyRole(['HR', 'Broker', 'CRM']);
  }

  /**
   * Check if user can process claims
   */
  canProcessClaims() {
    return this.hasRole('Claims_Officer');
  }

  /**
   * Check if user can approve claims
   */
  canApproveClaims() {
    return this.hasRole('Approver');
  }

  /**
   * Check if user can manage users
   */
  canManageUsers() {
    return this.isAdmin();
  }

  /**
   * Check if user can view all claims
   */
  canViewAllClaims() {
    return this.hasAnyRole(['Claims_Officer', 'Approver', 'Finance']);
  }

  /**
   * Check if user can view claims (either all or their own)
   */
  canViewClaims() {
    return this.canViewAllClaims() || this.canSubmitClaims();
  }

  /**
   * Check if user can view financial data
   */
  canViewFinancials() {
    return this.hasAnyRole(['Finance', 'Approver']);
  }

  /**
   * Check if user has submission-only role
   */
  hasSubmissionOnlyRole() {
    const hasSubmissionRole = this.hasAnyRole(['HR', 'Broker', 'CRM']);
    const hasProcessingRole = this.hasAnyRole(['Claims_Officer', 'Approver', 'Finance', 'Admin']);
    return hasSubmissionRole && !hasProcessingRole;
  }

  /**
   * Check if user is Claims Officer
   */
  isClaimsOfficer() {
    return this.hasRole('Claims_Officer');
  }

  /**
   * Check if user can take ownership of claims
   */
  canTakeClaimOwnership() {
    return this.isClaimsOfficer();
  }

  /**
   * Check if user can request documents
   */
  canRequestDocuments() {
    return this.isClaimsOfficer();
  }

  /**
   * Check if user can recommend for approval
   */
  canRecommendForApproval() {
    return this.isClaimsOfficer();
  }

  /**
   * Check if user can approve benefit amounts
   */
  canApproveBenefitAmounts() {
    return this.isClaimsOfficer();
  }

  /**
   * Check if user can view benefit amounts based on role and approval status
   */
  canViewBenefitAmounts(claim = null) {
    // Claims Officers, Approvers, Finance, and Admins can always see benefit amounts
    const canAlwaysSeeBenefits = this.hasAnyRole(['Claims_Officer', 'Approver', 'Finance', 'Admin']);
    
    if (canAlwaysSeeBenefits) {
      return true;
    }
    
    // If no claim provided, assume they can see if they have processing roles
    if (!claim) {
      return canAlwaysSeeBenefits;
    }
    
    // For HR/CRM/Broker roles, show amount only if it's been approved by Claims Officer
    const isBenefitApproved = claim.benefitApprovedAt || claim.approvedBenefitAmount !== null;
    
    return isBenefitApproved;
  }

  /**
   * Get allowed claim statuses for user role
   */
  getAllowedClaimStatuses() {
    const allStatuses = Object.values(CLAIM_STATUSES);
    
    if (this.canViewAllClaims()) {
      return allStatuses;
    }

    if (this.canSubmitClaims()) {
      // Submitters can see most statuses but may have limited view
      return allStatuses;
    }

    return allStatuses;
  }

  /**
   * Get role-based dashboard sections visibility
   */
  getDashboardVisibility() {
    return {
      statistics: {
        totalClaims: this.canViewClaims(),
        pendingClaims: this.canProcessClaims(),
        approvedClaims: this.canApproveClaims(),
        totalValue: this.canViewFinancials(),
        totalUsers: this.canManageUsers(),
        activeUsers: this.canManageUsers()
      },
      quickActions: {
        submitClaim: this.canSubmitClaims(),
        processClaimsAndDocuments: this.canProcessClaims(), // Combined action for Claims Officers
        approveBenefitAmounts: this.canApproveBenefitAmounts(), // New benefit approval action
        approveClaims: this.canApproveClaims(),
        managePayments: this.hasRole('Finance'),
        manageUsers: this.canManageUsers(),
        viewReports: this.canViewFinancials()
      },
      recentActivity: this.canViewAllClaims()
    };
  }

  /**
   * Get role-based navigation items
   */
  getNavigationItems() {
    const items = [];

    // Dashboard - always available
    items.push({
      name: 'Dashboard',
      path: '/dashboard',
      icon: 'dashboard',
      roles: '*'
    });

    // Claims section
    if (this.canViewClaims()) {
      items.push({
        name: this.hasSubmissionOnlyRole() ? 'My Claims' : 'Claims',
        path: '/claims',
        icon: 'claims',
        roles: ['HR', 'Broker', 'CRM', 'Claims_Officer', 'Approver', 'Finance']
      });
    }

    // Submit claim
    if (this.canSubmitClaims()) {
      items.push({
        name: 'Submit Claim',
        path: '/claims/submit',
        icon: 'submit',
        roles: ['HR', 'Broker', 'CRM']
      });
    }

    // Document management
    if (this.canProcessClaims()) {
      items.push({
        name: 'Documents',
        path: '/documents',
        icon: 'documents',
        roles: ['Claims_Officer', 'Approver']
      });
    }

    // Payments
    if (this.hasRole('Finance')) {
      items.push({
        name: 'Payments',
        path: '/payments',
        icon: 'payments',
        roles: ['Finance']
      });
    }

    // Reports
    if (this.canViewFinancials()) {
      items.push({
        name: 'Reports',
        path: '/reports',
        icon: 'reports',
        roles: ['Finance', 'Approver']
      });
    }

    // User management
    if (this.canManageUsers()) {
      items.push({
        name: 'Users',
        path: '/users',
        icon: 'users',
        roles: ['Admin']
      });
    }

    // Analytics
    if (this.isAdmin()) {
      items.push({
        name: 'Analytics',
        path: '/analytics',
        icon: 'analytics',
        roles: ['Admin']
      });
    }

    return items;
  }
}

/**
 * Hook for using role-based filtering
 */
export const useRoleBasedFiltering = (userRoles, user) => {
  const filtering = new RoleBasedFiltering(userRoles, user);
  
  return {
    filtering,
    permissions: {
      canSubmitClaims: filtering.canSubmitClaims(),
      canProcessClaims: filtering.canProcessClaims(),
      canApproveClaims: filtering.canApproveClaims(),
      canApproveBenefitAmounts: filtering.canApproveBenefitAmounts(),
      canManageUsers: filtering.canManageUsers(),
      canViewAllClaims: filtering.canViewAllClaims(),
      canViewFinancials: filtering.canViewFinancials(),
      hasSubmissionOnlyRole: filtering.hasSubmissionOnlyRole()
    },
    dashboardVisibility: filtering.getDashboardVisibility(),
    navigationItems: filtering.getNavigationItems()
  };
};

export default RoleBasedFiltering;
